/**
 * useSeo — centralny hook SEO dla Bukietowej.
 *
 * ETAP 1: document.title, meta description, robots, Open Graph, Twitter
 * ETAP 2: <link rel="canonical"> — absolutny URL produkcyjny, bez query params
 *
 * Canonical generowany z window.location.pathname (bez query string)
 * z wyciętym prefiksem BASE_URL (np. '/bukietowa' w dev, '' w prod).
 * Nie wymaga kontekstu Routera — działa w każdym komponencie.
 *
 * Domena produkcyjna: https://bukietowa.pl
 * Canonical zawsze wskazuje na bukietowa.pl — niezależnie od środowiska.
 * Na non-production (staging.bukietowa.pl, *.workers.dev, localhost itp.)
 * robots jest wymuszany na "noindex, nofollow" aby zapobiec indeksowaniu.
 *
 * Użycie:
 *   useSeo({ title: 'Bukiety kwiatów', description: 'Opis...' });
 *
 * Parametr `noSuffix: true` wyłącza automatyczne „| Bukietowa".
 */
import { useEffect } from 'react';
import { canonicalPath, PROD_ORIGIN } from '@workspace/url-source';
import { cmsPageByPath, serializeCmsJson, type CmsField } from '@workspace/url-source/cms';
import { cmsPublicPath, useCmsFields } from '@/cms/content';
import { seoRobots } from './seoRobots';
import { displayText } from './displayText';

const SUFFIX       = ' | Bukietowa';
const DEFAULT_DESC =
  'Bukietowa to kwiaciarnia w Lublinie. Zamawiaj świeże kwiaty i bukiety online z dostawą tego samego dnia na terenie Lublina i okolic.';

/**
 * Normal sessions follow their hostname. A localhost snapshot can explicitly
 * target production; the build flag is never embedded into the published HTML.
 */
function resolveRobots(robots?: string): string {
  const runtime = window as Window & { __PRERENDER_MODE__?: boolean; __PRERENDER_SITE_ORIGIN__?: string };
  return seoRobots(window.location.hostname, robots,
    runtime.__PRERENDER_MODE__ ? runtime.__PRERENDER_SITE_ORIGIN__ : undefined);
}

// BASE_URL z Vite (np. '/bukietowa/' w dev, '/' w prod).
// Wycinamy trailing slash aby ułatwić porównanie z pathname.
const BASE_PREFIX = import.meta.env.BASE_URL.replace(/\/$/, ''); // np. '/bukietowa' lub ''

export interface SeoParams {
  title: string;
  description?: string;
  robots?: string;
  ogType?: string;
  ogImage?: string;
  canonical?: string;
  noSuffix?: boolean;
}

/** Stripped-HTML utility — dla product.descriptionHtml → meta description */
export function stripHtml(html: string, maxLen = 155): string {
  const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return text.length > maxLen ? text.slice(0, maxLen - 1) + '\u2026' : text;
}

function setMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href: string): void {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

/**
 * Zwraca czystą ścieżkę aplikacji bez prefiksu BASE i bez query string.
 * Przykład (dev):   '/bukietowa/collections/Bukiety?sort=asc' → '/collections/Bukiety'
 * Przykład (prod):  '/collections/Bukiety?sort=asc'           → '/collections/Bukiety'
 */
function getCleanPath(): string {
  const raw = cmsPublicPath(); // publiczna ścieżka również w chronionym podglądzie
  const path = BASE_PREFIX ? raw.slice(BASE_PREFIX.length) : raw;
  return canonicalPath(path || '/');
}

export function useSeo({
  title,
  description,
  robots,
  ogType,
  ogImage,
  canonical,
  noSuffix = false,
}: SeoParams): void {
  const fields: CmsField[] = [
    { key: 'seo.title', label: 'Tytuł SEO', section: 'SEO', kind: 'text', defaultValue: title, required: true, maxLength: 200 },
    { key: 'seo.description', label: 'Opis SEO', section: 'SEO', kind: 'textarea', defaultValue: description ?? DEFAULT_DESC, required: true, maxLength: 500 },
    { key: 'seo.image', label: 'Obraz udostępniania', section: 'SEO', kind: 'image', defaultValue: ogImage ?? '' },
  ];
  const cms = useCmsFields(fields);
  const cmsTitle = cms.value('seo.title');
  const cmsDescription = cms.value('seo.description');
  const cmsImage = cms.value('seo.image');
  useEffect(() => {
    const fullTitle    = displayText(noSuffix ? cmsTitle : cmsTitle + SUFFIX);
    const desc         = displayText(cmsDescription);
    const type         = ogType ?? 'website';

    // ── Document title ────────────────────────────────────────────
    document.title = fullTitle;
    setMeta('name', 'description', desc);

    // ── Robots — na non-production wymuszamy noindex, nofollow ────
    // Staging (staging.bukietowa.pl), *.workers.dev, localhost itp.
    // nie mogą trafić do indeksu Google. Na produkcji (bukietowa.pl)
    // używamy wartości przekazanej przez stronę (lub nie ustawiamy headera).
    const effectiveRobots = resolveRobots(robots);
    setMeta('name', 'robots', effectiveRobots);

    // ── Canonical — zawsze absolutny URL produkcyjny (bukietowa.pl) ──
    // Na stagingu canonical celowo wskazuje na bukietową.pl — zapobiega
    // duplikacji treści w Google nawet gdyby strona staging trafiła do indeksu.
    const canonicalUrl = canonical ?? PROD_ORIGIN + getCleanPath();
    setCanonical(canonicalUrl);

    // ── Open Graph ────────────────────────────────────────────────
    setMeta('property', 'og:title',       fullTitle);
    setMeta('property', 'og:description', desc);
    setMeta('property', 'og:type',        type);
    setMeta('property', 'og:url',         canonicalUrl);
    if (cmsImage) setMeta('property', 'og:image', cmsImage.startsWith('/') ? PROD_ORIGIN + cmsImage : cmsImage);
    else document.querySelector('meta[property="og:image"]')?.remove();

    // ── Twitter ───────────────────────────────────────────────────
    setMeta('name', 'twitter:title',       fullTitle);
    setMeta('name', 'twitter:description', desc);
    if (cmsImage) setMeta('name', 'twitter:image', cmsImage.startsWith('/') ? PROD_ORIGIN + cmsImage : cmsImage);
    else document.querySelector('meta[name="twitter:image"]')?.remove();

    document.getElementById('bukietowa-cms-seo-schema')?.remove();
    if (cmsPageByPath(getCleanPath())) {
      const element = document.createElement('script');
      element.type = 'application/json'; element.id = 'bukietowa-cms-seo-schema';
      element.setAttribute('data-cms-fields', '1');
      element.setAttribute('data-cms-robots', robots ?? 'index, follow');
      element.setAttribute('data-cms-no-suffix', String(noSuffix));
      element.textContent = serializeCmsJson(fields);
      document.head.appendChild(element);
    }

  }, [title, description, robots, ogType, ogImage, canonical, noSuffix, cmsTitle, cmsDescription, cmsImage]);
}
